import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { Head } from '~/components/shared/Head';
import { CollabHeader } from './components/CollabHeader';
import { ConversationCard } from './components/ConversationCard';
import { LIST_CONVERSATIONS } from '~/services/conversations';
import { motion } from 'motion/react';
import moment from 'moment';
import 'moment/locale/es';

export default function CollabDashboard() {
  const { agentId = '' } = useParams();
  const { t, i18n } = useTranslation('collab');
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState({
    required: true,
    optional: true,
    noneed: true,
  });

  // Query to fetch open conversations
  const { data, loading, networkStatus, error, refetch } = useQuery(
    LIST_CONVERSATIONS,
    {
      variables: {
        data: {
          agent_id: agentId,
          filters: {
            is_open: true, // Only fetch open conversations
          },
          limit: 50,
        },
      },
      skip: !agentId,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
  );

  const conversations = data?.response?.data?.results || [];
  const totalCount = data?.response?.data?.total_count ?? conversations.length;

  // Helpers
  useEffect(() => {
    // Sync moment locale with i18n
    moment.locale(i18n.language?.startsWith('es') ? 'es' : 'en');
  }, [i18n.language]);

  const formatTimeAgo = (dateInput: string | number | Date | undefined) => {
    if (!dateInput) return '';
    return moment(dateInput).fromNow();
  };

  const getLatestReason = (conversation: any): string => {
    const history = conversation?.classification_history || [];
    if (!history.length) return '';
    const last = history[history.length - 1];
    return last?.intervention?.reason || '';
  };

  const filteredConversations = useMemo(() => {
    if (!searchTerm) return conversations;
    const searchLower = searchTerm.toLowerCase();
    return conversations.filter((c: any) => {
      const tag = c.current_primary_tag?.tag_name?.toLowerCase() || '';
      const reason = getLatestReason(c).toLowerCase();
      const personId = String(c.person_id || '').toLowerCase();
      return (
        tag.includes(searchLower) ||
        reason.includes(searchLower) ||
        personId.includes(searchLower)
      );
    });
  }, [conversations, searchTerm]);

  const grouped = useMemo(() => {
    const req: any[] = [];
    const opt: any[] = [];
    const noneed: any[] = [];
    for (const c of filteredConversations) {
      const bucket = c?.stable_intervention?.bucket || 'NO_NEED';
      if (bucket === 'REQUIRED') req.push(c);
      else if (bucket === 'OPTIONAL') opt.push(c);
      else noneed.push(c);
    }
    // Sort by updated_at desc within each bucket for recency
    const sorter = (a: any, b: any) =>
      new Date(b.updated_at || 0).getTime() -
      new Date(a.updated_at || 0).getTime();
    return {
      REQUIRED: req.sort(sorter),
      OPTIONAL: opt.sort(sorter),
      NO_NEED: noneed.sort(sorter),
    };
  }, [filteredConversations]);

  return (
    <div className="flex h-full w-full flex-col">
      <Head title={t('open_conversations', 'Open conversations')} />

      <motion.div
        className="flex h-full flex-col"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <CollabHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onRefresh={() => refetch()}
          isLoading={loading || networkStatus === 4}
        />

        {/* Totals + Columns container */}
        <div className="mb-3 flex items-center justify-end px-2">
          <span className="text-base-content/60 text-xs">
            {t('open_conversations', 'Open conversations')}: {totalCount}
          </span>
        </div>

        {/* Desktop: Three columns, Mobile: Single column */}
        <div className="flex h-full flex-col gap-4 overflow-hidden px-2 md:flex-row">
          {/* Column: REQUIRED */}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col md:flex-1">
            <div className="min-h-0 flex-1 overflow-hidden pr-1 md:flex-1">
              <div
                className={`collapse-arrow bg-base-100 border-base-200 collapse flex flex-col overflow-hidden border ${
                  open.required ? 'collapse-open' : 'collapse-close'
                }`}
                style={{
                  height: open.required ? '100%' : 'auto',
                }}
              >
                <div
                  className="collapse-title flex cursor-pointer items-center justify-between text-sm font-medium"
                  onClick={() =>
                    setOpen((s) => ({ ...s, required: !s.required }))
                  }
                >
                  <span>{t('required_section', 'Required')}</span>
                  <span className="badge badge-primary badge-sm">
                    {grouped.REQUIRED.length}
                  </span>
                </div>
                <div className="collapse-content">
                  <div className="flex max-h-[calc(100vh-220px)] flex-col gap-3 overflow-y-auto">
                    {grouped.REQUIRED.map((c: any, idx: number) => (
                      <motion.div
                        key={c._id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25, delay: idx * 0.02 }}
                      >
                        <ConversationCard
                          conversation={c}
                          bucket="REQUIRED"
                          formatTimeAgo={formatTimeAgo}
                          getLatestReason={getLatestReason}
                        />
                      </motion.div>
                    ))}
                    {!loading && grouped.REQUIRED.length === 0 && (
                      <div className="text-base-content/60 rounded-md border border-dashed p-4 text-center text-xs">
                        No conversations
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Column: OPTIONAL */}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col md:flex-1">
            <div className="min-h-0 flex-1 overflow-hidden pr-1 md:flex-1">
              <div
                className={`collapse-arrow bg-base-100 border-base-200 collapse flex flex-col overflow-hidden border ${
                  open.optional ? 'collapse-open' : 'collapse-close'
                }`}
                style={{
                  height: open.optional ? '100%' : 'auto',
                }}
              >
                <div
                  className="collapse-title flex cursor-pointer items-center justify-between text-sm font-medium"
                  onClick={() =>
                    setOpen((s) => ({ ...s, optional: !s.optional }))
                  }
                >
                  <span>{t('optional_section', 'Optional')}</span>
                  <span className="badge badge-secondary badge-sm">
                    {grouped.OPTIONAL.length}
                  </span>
                </div>
                <div className="collapse-content">
                  <div className="flex max-h-[calc(100vh-220px)] flex-col gap-3 overflow-y-auto">
                    {grouped.OPTIONAL.map((c: any, idx: number) => (
                      <motion.div
                        key={c._id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25, delay: idx * 0.02 }}
                      >
                        <ConversationCard
                          conversation={c}
                          bucket="OPTIONAL"
                          formatTimeAgo={formatTimeAgo}
                          getLatestReason={getLatestReason}
                        />
                      </motion.div>
                    ))}
                    {!loading && grouped.OPTIONAL.length === 0 && (
                      <div className="text-base-content/60 rounded-md border border-dashed p-4 text-center text-xs">
                        No conversations
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Column: NO_NEED */}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col md:flex-1">
            <div className="min-h-0 flex-1 overflow-hidden pr-1 md:flex-1">
              <div
                className={`collapse-arrow bg-base-100 border-base-200 collapse flex flex-col overflow-hidden border ${
                  open.noneed ? 'collapse-open' : 'collapse-close'
                }`}
                style={{
                  height: open.noneed ? '100%' : 'auto',
                }}
              >
                <div
                  className="collapse-title flex cursor-pointer items-center justify-between text-sm font-medium"
                  onClick={() => setOpen((s) => ({ ...s, noneed: !s.noneed }))}
                >
                  <span>{t('no_need_section', 'No need')}</span>
                  <span className="badge badge-accent badge-sm">
                    {grouped.NO_NEED.length}
                  </span>
                </div>
                <div className="collapse-content">
                  <div className="flex max-h-[calc(100vh-220px)] flex-col gap-3 overflow-y-auto">
                    {grouped.NO_NEED.map((c: any, idx: number) => (
                      <motion.div
                        key={c._id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25, delay: idx * 0.02 }}
                      >
                        <ConversationCard
                          conversation={c}
                          bucket="NO_NEED"
                          formatTimeAgo={formatTimeAgo}
                          getLatestReason={getLatestReason}
                        />
                      </motion.div>
                    ))}
                    {!loading && grouped.NO_NEED.length === 0 && (
                      <div className="text-base-content/60 rounded-md border border-dashed p-4 text-center text-xs">
                        No conversations
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
